/**
 * Owns the lifecycle of this device's push registration, free of React and of any provider SDK.
 * It talks to a `client` (permission, token, platform) and an `api` (register, unregister), so the
 * rules below are testable on their own and the transport can change without touching them.
 *
 * `preferences` holds per-device choices (opt-out, invite dismissal) that survives sign-out: someone who switched push
 * off on a phone must not have it silently switched back on at the next login just because the OS
 * permission is still granted.
 */
export const createPushRegistrar = ({
  client,
  api,
  preferences,
  readAuthToken,
  logger = console,
}) => {
  let registeredToken = null;

  const registerCurrentDevice = async () => {
    const token = await client.obtainToken();
    await api.register({ token, platform: client.platform });
    registeredToken = token;
  };

  const readState = async () => {
    const supported = await client.isSupported();
    return {
      supported,
      permission: supported ? await client.getPermission() : "denied",
      registered: registeredToken !== null,
      optedOut: preferences.isOptedOut(),
    };
  };

  /**
   * Whether to offer push in the UI: only while the choice is still open, the user has not waved the
   * offer away on this device, and push is not already off or blocked.
   */
  const withInvite = (state) => ({
    ...state,
    invite:
      state.supported &&
      !state.optedOut &&
      state.permission === "prompt" &&
      !preferences.isInviteDismissed(),
  });

  /** The token this device holds, without ever prompting for permission to learn it. */
  const knownToken = async () => {
    if (registeredToken) return registeredToken;
    if (!(await client.isSupported())) return null;
    return (await client.getPermission()) === "granted" ? client.obtainToken() : null;
  };

  const status = async () => withInvite(await readState());

  return {
    status,

    /**
     * Run once per signed-in session: refreshes the token when permission is already granted. It never
     * asks; the user is asked through the invite, so the system dialog arrives with an explanation.
     */
    async sync() {
      if (!readAuthToken() || !(await client.isSupported()) || preferences.isOptedOut())
        return status();
      if ((await client.getPermission()) === "granted") await registerCurrentDevice();
      return status();
    },

    /** "Not now" on the invite: stays quiet on this device; Settings still offers the switch. */
    async dismissInvite() {
      preferences.dismissInvite();
      return status();
    },

    /** Must be called from a user gesture: browsers refuse permission prompts otherwise. */
    async enable() {
      if (!readAuthToken() || !(await client.isSupported())) return status();
      const permission = await client.requestPermission();
      if (permission === "granted") {
        preferences.setOptedOut(false);
        await registerCurrentDevice();
      }
      return status();
    },

    async disable() {
      preferences.setOptedOut(true);
      const token = await knownToken();
      if (token) await api.unregister({ token });
      await client.deleteToken();
      registeredToken = null;
      return status();
    },

    /**
     * Sign-out: stop this device receiving the account's pushes. The auth token is read before
     * the session storage is cleared, since the request outlives it. Never throws, because a
     * failure here must not block signing out; a stale token is pruned by the provider later.
     */
    async release() {
      const authToken = readAuthToken();
      const known = registeredToken;
      registeredToken = null;
      if (!authToken) return;
      try {
        const token = known ?? (await knownToken());
        if (!token) return;
        await api.unregister({ token, authToken });
      } catch (error) {
        logger.warn("Could not unregister this device from push notifications", error);
      }
    },
  };
};
